import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { GrowerFileKind, GrowerFileRecord } from '@/lib/api/growers-api'
import { growerKeys, useGrower } from '@/hooks/use-growers-query'
import { growersApi } from '@/lib/api/growers-api'
import { fileDownloadUrl, uploadFile } from '@/lib/api/files-api'

export type DraftItem = {
    key: string
    name: string
    url: string
    isImage: boolean
    // 'existing' đã ở server; 'pending' mới chọn, chưa upload.
    kind: 'existing' | 'pending'
    fileId?: string
    /** Dung lượng (byte); có thì ô đính kèm hiện kèm dưới tên tệp. */
    size?: number
}

const IMAGE_RE = /\.(jpe?g|png|gif|webp|bmp|svg)$/i

/** Whether a stored file name renders as an image (preview vs file badge). */
export function isImageName(name?: string): boolean {
    return !!name && IMAGE_RE.test(name)
}

export type PendingFile = { tempId: string; file: File; url: string }

let pendingSeq = 0

/**
 * Khoá cho một tệp vừa chọn, dùng làm React key và làm mốc để xoá đúng mục.
 *
 * Đếm tăng dần chứ KHÔNG lấy `${tên}-${cỡ}-${độ dài mảng}`: chọn cùng một tệp
 * hai lần rồi xoá cái đầu thì lần chọn thứ ba lại sinh đúng khoá của mục còn
 * lại — hai mục trùng khoá, bấm xoá một cái là mất cả hai.
 */
export function nextPendingId(file: File): string {
    pendingSeq += 1
    return `${file.name}-${file.size}-${pendingSeq}`
}

/**
 * Tệp này có thuộc đúng ô đang xét không. Một hộ giữ mọi giấy tờ trong cùng
 * `grower.files`, nên ô nào cũng phải tự lọc phần của mình theo HAI trục:
 * `kind` (hồ sơ hay giấy tờ đất) và `area_id` (vùng trồng nào giữ nó).
 *
 * Bản ghi cũ chưa có `kind` là giấy tờ hồ sơ. Giấy tờ đất `area_id` rỗng là tệp
 * mồ côi từ trước khi tách theo vùng (hộ chưa khai vùng nào lúc chuyển đổi) —
 * không ô nào nhận, đúng ý: hộ khai vùng xong thì tải lại.
 */
export function matchesSlot(
    file: GrowerFileRecord,
    kind: GrowerFileKind,
    areaId?: string,
): boolean {
    if ((file.kind ?? 'profile') !== kind) return false
    return (file.area_id ?? undefined) === areaId
}

/**
 * Dựng danh sách hiển thị của một ô đính kèm: tệp đã lưu (trừ tệp vừa bấm xoá)
 * rồi tới tệp mới chọn. Tách khỏi hook để ô giấy tờ đất theo vùng — vốn không
 * gọi được một hook cho mỗi vùng — dùng lại đúng logic này thay vì chép lại.
 */
export function buildDraftItems(
    files: GrowerFileRecord[] | undefined,
    kind: GrowerFileKind,
    areaId: string | undefined,
    removedIds: string[],
    pending: PendingFile[],
): DraftItem[] {
    const existing: DraftItem[] = (files ?? [])
        .filter((f) => matchesSlot(f, kind, areaId))
        .filter((f) => !removedIds.includes(f.id))
        .map((f) => ({
            key: `ex-${f.id}`,
            name: f.name ?? f.id,
            url: fileDownloadUrl(f.id),
            isImage: isImageName(f.name),
            kind: 'existing' as const,
            fileId: f.id,
        }))
    const pendingItems: DraftItem[] = pending.map((p) => ({
        key: `pd-${p.tempId}`,
        name: p.file.name,
        url: p.url,
        isImage: isImageName(p.file.name) || p.file.type.startsWith('image/'),
        kind: 'pending' as const,
    }))
    return [...existing, ...pendingItems]
}

/**
 * Áp thay đổi của một ô lên server: gỡ tệp đã bấm xoá rồi tải tệp mới lên.
 *
 * Gỡ TRƯỚC rồi mới tải lên, giữ đúng thứ tự cũ: người dùng thay một tệp bằng
 * tệp khác thì bản cũ phải biến mất kể cả khi bản mới tải hỏng.
 */
export async function commitDraftChanges(opts: {
    growerId: string
    kind: GrowerFileKind
    areaId?: string
    removedIds: string[]
    pending: PendingFile[]
}): Promise<void> {
    for (const id of opts.removedIds) {
        await growersApi.detachFile(opts.growerId, id)
    }
    for (const p of opts.pending) {
        // Giấy tờ nhạy cảm (CCCD, bìa đỏ) — không public qua mount công khai.
        const rec = await uploadFile(p.file, false)
        await growersApi.attachFile(
            opts.growerId,
            rec.id,
            opts.kind,
            opts.areaId,
        )
        URL.revokeObjectURL(p.url)
    }
}

/**
 * Bỏ các tệp nháp chưa tải lên và thu hồi objectURL của chúng. Quên thu hồi là
 * rò rỉ bộ nhớ đúng bằng dung lượng ảnh người dùng vừa chọn.
 */
export function revokePending(pending: PendingFile[]): void {
    pending.forEach((p) => URL.revokeObjectURL(p.url))
}

/**
 * Quản lý giấy tờ đính kèm dạng "nháp": thêm/xoá chỉ giữ ở client, chỉ thực sự
 * upload/detach khi gọi commit() (sau khi lưu hộ). Dùng chung cho tạo & sửa.
 *
 * `kind` chọn ô upload: màn hồ sơ hộ có 2 ô (giấy tờ hồ sơ và giấy tờ đất của
 * vùng trồng) cùng đọc `grower.files`, nên mỗi draft phải lọc phần của mình —
 * không lọc thì thêm tệp ở ô này lại hiện luôn ở ô kia. Mặc định 'profile' để
 * call-site cũ giữ nguyên hành vi.
 */
export function useAttachmentDraft(
    growerId?: string,
    kind: GrowerFileKind = 'profile',
) {
    const qc = useQueryClient()
    const { data: detail } = useGrower(growerId)
    const [removedIds, setRemovedIds] = useState<string[]>([])
    const [pending, setPending] = useState<PendingFile[]>([])
    const pendingRef = useRef<PendingFile[]>([])
    pendingRef.current = pending

    // Thu hồi objectURL khi unmount để tránh rò rỉ bộ nhớ.
    useEffect(() => {
        return () => revokePending(pendingRef.current)
    }, [])

    const items = buildDraftItems(
        detail?.files,
        kind,
        undefined,
        removedIds,
        pending,
    )

    const addFiles = useCallback((files: FileList | File[]) => {
        const arr = Array.from(files)
        if (arr.length === 0) return
        setPending((prev) => [
            ...prev,
            ...arr.map((file) => ({
                tempId: nextPendingId(file),
                file,
                url: URL.createObjectURL(file),
            })),
        ])
    }, [])

    const remove = useCallback((item: DraftItem) => {
        if (item.kind === 'existing' && item.fileId) {
            const id = item.fileId
            setRemovedIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
        } else {
            setPending((prev) => {
                const found = prev.find((p) => `pd-${p.tempId}` === item.key)
                if (found) URL.revokeObjectURL(found.url)
                return prev.filter((p) => `pd-${p.tempId}` !== item.key)
            })
        }
    }, [])

    const hasChanges = pending.length > 0 || removedIds.length > 0

    /** Áp thay đổi lên server sau khi hộ đã được lưu (cần growerId thật). */
    const commit = useCallback(
        async (gid: string) => {
            if (!gid) return
            await commitDraftChanges({
                growerId: gid,
                kind,
                removedIds,
                pending,
            })
            setPending([])
            setRemovedIds([])
            await qc.invalidateQueries({ queryKey: growerKeys.detail(gid) })
            await qc.invalidateQueries({ queryKey: growerKeys.lists() })
        },
        [pending, removedIds, qc, kind],
    )

    return { items, addFiles, remove, commit, hasChanges }
}

export type AttachmentDraft = ReturnType<typeof useAttachmentDraft>
